import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useGetLoginInfo } from '@multiversx/sdk-dapp/hooks/account'
import { ZoidCore } from '@zoidpay-tech/zoidcore-web3-toolkit'
import { EsdtLocalBurn } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/esdt/localBurn'
import { EsdtWipe } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/esdt/esdtWipe'
import { EsdtLocalMint } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/esdt/localMint'
import { EsdtPause } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/esdt/esdtPause'
import { EsdtUnPause } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/esdt/esdtUnPause'
import { EsdtFreeze } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/esdt/esdtFreeze'
import { EsdtUnFreeze } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/esdt/esdtUnFreeze'
import { EsdtControlChanges } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/esdt/esdtControlChanges'
import { useTrackTransactionStatus } from '@multiversx/sdk-dapp/hooks/transactions'
import { getAddress } from '@multiversx/sdk-dapp/utils/account'
import { sendTransactions } from '@multiversx/sdk-dapp/services'

import { useAuthState } from 'src/contexts/authContext'
import { mvxNetworkProvidersType } from 'src/util/types'
import { mvxNetworkProviders } from 'src/util/networksRpcs'

export interface IWipeEsdtToken {
  type: 'wipe'
  txId?: string
  address?: string
  tokenTicker: string
  addressToWipe: string
  network: string
}

export interface IBurnEsdtToken {
  type: 'burn'
  txId?: string
  address?: string
  tokenTicker?: string
  supply: number
  smartContractAddress?: string
  network: string
  chain?: string
}

export interface IMintEsdtToken {
  type: 'mint'
  txId?: string
  address?: string
  tokenTicker?: string
  supply: number
  smartContractAddress?: string
  network: string
  chain?: string
}

export interface IPauseEsdtToken {
  type: 'pause' | 'unpause'
  txId?: string
  address?: string
  tokenTicker: string
  network: string
}

export interface IFreezeEsdtToken {
  type: 'freeze' | 'unfreeze'
  txId?: string
  address: string
  tokenTicker: string
  addressToFreeze?: string
  addressToUnfreeze?: string
  network: string
}

export interface IUpdateEsdtToken {
  type: 'update'
  txId?: string
  address?: string
  tokenTicker: string
  network: string
  canMint: boolean
  canBurn: boolean
  canPause?: boolean
  canFreeze?: boolean
  canWipe?: boolean
  canChangeOwner?: boolean
  canUpgrade?: boolean
  canAddSpecialRoles?: boolean
}

const useEsdtToken = ({ payloadKey }: { payloadKey: string }) => {
  const [payload, setPayload] = useState<
    IBurnEsdtToken | IWipeEsdtToken | IMintEsdtToken | IPauseEsdtToken
  >()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { isLoggedIn } = useGetLoginInfo()
  const user = useAuthState().user

  let zoid = new ZoidCore({
    apiKey: '',
    authorization: user?.accessToken,
    env: process.env.REACT_APP_ENVIRONMENT === 'development' ? 'debug' : '',
  })

  useEffect(() => {
    try {
      const sessionPayload = JSON.parse(sessionStorage.getItem(payloadKey) || '{}')
      setPayload(sessionPayload)
    } catch (err) {
      console.error('err', err)
    }
  }, [])

  useTrackTransactionStatus({
    transactionId: payload?.txId as string,
    onSuccess: async () => {
      sessionStorage.removeItem(payloadKey)
      setIsLoading(false)
    },
    onFail(transactionId, err) {
      sessionStorage.removeItem(payloadKey)
      toast.error(`${err} on ${transactionId}`, {
        progressClassName: 'simpleBar',
      })
    },
  })

  const handleToken = async (
    data:
      | IBurnEsdtToken
      | IWipeEsdtToken
      | IMintEsdtToken
      | IPauseEsdtToken
      | IFreezeEsdtToken
      | IUpdateEsdtToken,
  ) => {
    setIsLoading(true)
    try {
      const address = await getAddress()
      let response
      if (data.type === 'burn') {
        const burnPayload: EsdtLocalBurn = {
          address,
          senderAddress: address,
          tokenTicker: data.tokenTicker || '',
          networkProvider: mvxNetworkProviders[data.network as keyof mvxNetworkProvidersType],
          supplyToBurn: Number(data.supply),
        }

        response = await zoid.multiversX.esdtLocalBurn(burnPayload)
      } else if (data.type === 'wipe') {
        const wipePayload: EsdtWipe = {
          address,
          tokenTicker: data.tokenTicker,
          networkProvider: mvxNetworkProviders[data.network as keyof mvxNetworkProvidersType],
          addressToWipe: data.addressToWipe,
        }

        response = await zoid.multiversX.esdtWipe(wipePayload)
      } else if (data.type === 'mint') {
        const mintPayload: EsdtLocalMint = {
          address,
          senderAddress: address,
          tokenTicker: data.tokenTicker || '',
          networkProvider: mvxNetworkProviders[data.network as keyof mvxNetworkProvidersType],
          supplyToMint: Number(data.supply),
        }

        response = await zoid.multiversX.esdtLocalMint(mintPayload)
      } else if (data.type === 'pause') {
        const pausePayload: EsdtPause = {
          address,
          tokenTicker: data.tokenTicker,
          networkProvider: mvxNetworkProviders[data.network as keyof mvxNetworkProvidersType],
        }

        response = await zoid.multiversX.esdtPause(pausePayload)
      } else if (data.type === 'unpause') {
        const unpausePayload: EsdtUnPause = {
          address,
          tokenTicker: data.tokenTicker,
          networkProvider: mvxNetworkProviders[data.network as keyof mvxNetworkProvidersType],
        }

        response = await zoid.multiversX.esdtUnPause(unpausePayload)
      } else if (data.type === 'freeze') {
        const freezePayload: EsdtFreeze = {
          address,
          tokenTicker: data.tokenTicker,
          networkProvider: mvxNetworkProviders[data.network as keyof mvxNetworkProvidersType],
          addressToFreeze: data.addressToFreeze || '',
        }

        response = await zoid.multiversX.esdtFreeze(freezePayload)
      } else if (data.type === 'unfreeze') {
        const unfreezePayload: EsdtUnFreeze = {
          address,
          tokenTicker: data.tokenTicker,
          networkProvider: mvxNetworkProviders[data.network as keyof mvxNetworkProvidersType],
          addressToUnfreeze: data.addressToUnfreeze || '',
        }

        response = await zoid.multiversX.esdtUnFreeze(unfreezePayload)
      } else if (data.type === 'update') {
        const updatePayload: EsdtControlChanges = {
          address,
          tokenTicker: data.tokenTicker,
          networkProvider: mvxNetworkProviders[data.network as keyof mvxNetworkProvidersType],
          esdtProperties: {
            canMint: data.canMint,
            canBurn: data.canBurn,
            canPause: data.canPause || false,
            canFreeze: data.canFreeze || false,
            canWipe: data.canWipe || false,
            canChangeOwner: data.canChangeOwner || false,
            canUpgrade: data.canUpgrade || false,
            canAddSpecialRoles: data.canAddSpecialRoles || false,
          },
        }

        response = await zoid.multiversX.esdtControlChanges(updatePayload)
      }

      const { transaction, transactionData } = response.data.data

      const transactionRes = await sendTransactions({
        transactions: {
          data: transactionData.data,
          sender: transactionData.sender.bech32,
          receiver: transactionData.receiver.bech32,
          value: transaction.value,
          gasLimit: transaction.gasLimit,
        },
        transactionsDisplayInfo: {
          processingMessage: 'Processing Issue token transaction',
          errorMessage: 'An error has occurred during transaction',
          successMessage: 'Issue token transaction successful',
        },
        redirectAfterSign: false,
      })

      sessionStorage.setItem(
        payloadKey,
        JSON.stringify({
          ...payload,
          network: data.network,
          txId: transactionRes?.sessionId,
        }),
      )
    } catch (err) {
      setIsLoading(false)
      if (err instanceof Error) {
        toast.error(err?.message, {
          progressClassName: 'simpleBar',
        })
      }
    }
  }

  return { isLoading, isWalletConnected: isLoggedIn, handleToken }
}

export default useEsdtToken
